// Bonuses — admin page (Pending → Mark as Paid → Finalized) and staff "My Bonus".
import { useState, useEffect } from "react";
import { useMobile, showToast } from "../ui/core";
import { T, text, surface, money, fmtDay, fmtDateTime, period, methodLabel, METHODS } from "./ui/tokens";
import {
    StatusBadge, PageHeader, FinanceStatCard, StatGrid, Tabs, Notice, DataTable, ActionMenu, ExportMenu,
    Modal, Drawer, DetailList, FormSection, Field, Input, Select, Button, PaymentModal, AuditHistory,
} from "./ui/components";
import * as api from "./api";
import { payrollPolicy } from "./payroll";
import { downloadBonusSlip } from "./slips";
import { openProof } from "./proofs";

const isPaid = (b) => b.status === "paid";
// Paid before the new workflow existed (no finalization record) → shown as Legacy.
const bonusBadge = (b) => (!isPaid(b) ? "pending" : b.finalized_at ? "paid" : "legacy");
const bonusLabel = (b) => (!isPaid(b) ? "Pending" : b.finalized_at ? "Paid" : "Paid · Legacy");
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

function slip(ctx, b) {
    const s = ctx.staff.find(x => x.id === b.staff_id);
    return downloadBonusSlip({ bonus: b, employee: { name: s?.name || ctx.staffName(b.staff_id), designation: s?.designation } });
}

// ─── Add / edit bonus ────────────────────────────────────────────────────────
function BonusFormModal({ open, ctx, editing, onClose }) {
    const [form, setForm] = useState({ staff_id: "", amount: "", reason: "", date: todayISO() });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        if (!open) return;
        setErr("");
        setForm(editing
            ? { staff_id: editing.staff_id, amount: String(editing.amount), reason: editing.reason || "", date: editing.date || todayISO() }
            : { staff_id: ctx.staff[0]?.id || "", amount: "", reason: "", date: todayISO() });
    }, [open, editing, ctx.staff]);
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        setErr("");
        if (!form.staff_id) { setErr("Choose an employee."); return; }
        if (!(Number(form.amount) > 0)) { setErr("Enter the bonus amount."); return; }
        if (!form.date) { setErr("Choose the bonus date."); return; }
        setBusy(true);
        try {
            const d = new Date(`${form.date}T00:00:00`);
            const payload = { staff_id: form.staff_id, amount: Number(form.amount), reason: form.reason.trim(), date: form.date, month: d.getMonth() + 1, year: d.getFullYear() };
            if (editing) {
                await api.updateBonus(editing.id, payload);
                showToast("Bonus updated");
            } else {
                await api.addBonus({ ...payload, id: "bon" + Date.now(), status: "outstanding", created_at: new Date().toISOString() });
                await api.addNotification({
                    id: "notif_bon_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6), recipient_id: form.staff_id, type: "bonus",
                    title: "Bonus Received! 🎉", message: `You've received a bonus of Rs.${Number(form.amount).toLocaleString()}${form.reason ? ` — ${form.reason}` : ""}`,
                    read: false, created_at: new Date().toISOString(),
                }).catch(() => {});
                showToast(`Bonus of ${money(form.amount)} added for ${ctx.staffName(form.staff_id)}`);
            }
            await ctx.reload();
            onClose();
        } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };

    return (
        <Modal open={open} title={editing ? "Edit bonus" : "Add bonus"} onClose={busy ? undefined : onClose}
            footer={<>
                <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                <Button onClick={save} disabled={busy}>{busy ? "Saving…" : editing ? "Save changes" : "Add bonus"}</Button>
            </>}>
            <FormSection title="Bonus">
                <Field label="Employee" required>
                    <Select value={form.staff_id} onChange={set("staff_id")} disabled={!!editing}>
                        {ctx.staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </Select>
                </Field>
                <Field label="Amount (NPR)" required>
                    <Input type="number" inputMode="decimal" min="1" value={form.amount} onChange={set("amount")} placeholder="e.g. 5000" />
                </Field>
            </FormSection>
            <FormSection title="Details">
                <Field label="Reason"><Input value={form.reason} onChange={set("reason")} placeholder="e.g. Festival bonus, performance bonus" /></Field>
                <Field label="Bonus date" required><Input type="date" value={form.date} onChange={set("date")} /></Field>
            </FormSection>
            {err && <Notice tone="danger">{err}</Notice>}
        </Modal>
    );
}

// ─── Bonus details drawer ────────────────────────────────────────────────────
function BonusDrawer({ ctx, bonus, onClose }) {
    if (!bonus) return null;
    const b = bonus;
    return (
        <Drawer open title={`${ctx.staffName(b.staff_id)} · ${money(b.amount)}`} subtitle={b.reason || "Bonus"} onClose={onClose}
            actions={<Button size="sm" variant="secondary" onClick={() => slip(ctx, b)}>Slip</Button>}>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                <StatusBadge status={bonusBadge(b)} label={bonusLabel(b)} />
                {b.finalized_at && <StatusBadge status="finalized" />}
            </div>
            <h3 style={{ ...text.section, marginBottom: 10 }}>Bonus</h3>
            <div style={{ ...surface.inset, padding: 14, marginBottom: 20 }}>
                <DetailList items={[
                    ["Employee", ctx.staffName(b.staff_id)],
                    ["Amount", <strong>{money(b.amount)}</strong>],
                    ["Reason", b.reason || "—"],
                    ["Bonus date", fmtDay(b.date)],
                ]} />
            </div>
            {isPaid(b) && <>
                <h3 style={{ ...text.section, marginBottom: 10 }}>Payment</h3>
                <div style={{ ...surface.inset, padding: 14, marginBottom: 20 }}>
                    <DetailList items={[
                        ["Amount paid", <strong style={{ color: T.success }}>{money(b.paid_amount ?? b.amount)}</strong>],
                        ["Paid on", fmtDateTime(b.paid_at)],
                        ["Method", methodLabel(b.payment_method)],
                        ["Reference", b.txn_number || "—"],
                        ["Proof", b.payment_proof ? <a href="#" onClick={(e) => { e.preventDefault(); openProof(b.payment_proof); }} style={{ color: T.primary }}>View proof</a> : "—"],
                        b.paid_note && ["Notes", b.paid_note],
                        ["Processed by", b.paid_by ? ctx.staffName(b.paid_by) : "—"],
                    ]} />
                </div>
            </>}
            <h3 style={{ ...text.section, marginBottom: 10 }}>History</h3>
            <AuditHistory events={[
                { label: "Bonus created", by: b.created_by ? ctx.staffName(b.created_by) : null, at: b.created_at, tone: T.primary },
                isPaid(b) && { label: b.finalized_at ? "Marked paid" : "Marked paid (before payment details were recorded)", by: b.paid_by ? ctx.staffName(b.paid_by) : null, at: b.finalized_at || b.paid_at, tone: T.success },
            ]} />
        </Drawer>
    );
}

// ─── Admin: Bonuses page ─────────────────────────────────────────────────────
export function BonusAdminPage({ ctx }) {
    const mobile = useMobile();
    const [tab, setTab] = useState("pending");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [payFor, setPayFor] = useState(null);
    const [detailId, setDetailId] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [fStaff, setFStaff] = useState("all");
    const [fYear, setFYear] = useState("all");

    const all = ctx.bonuses;
    const pending = all.filter(b => !isPaid(b));
    const paid = all.filter(isPaid);
    const now = new Date();
    const thisMonth = all.filter(b => b.month === now.getMonth() + 1 && b.year === now.getFullYear());
    const years = [...new Set(all.map(b => b.year).filter(Boolean))].sort((a, b) => b - a);
    const base = tab === "pending" ? pending : tab === "paid" ? paid : all;
    const rows = base.filter(b => (fStaff === "all" || b.staff_id === fStaff) && (fYear === "all" || b.year === Number(fYear)));
    const sum = (list, f = (b) => b.amount) => list.reduce((a, b) => a + Number(f(b) || 0), 0);
    const detail = detailId ? all.find(b => b.id === detailId) : null;
    // Company figures count real employees only — test accounts are listed but never totalled.
    const isTest = (b) => payrollPolicy(ctx.staff.find(s => s.id === b.staff_id)).isTest;
    const real = (list) => list.filter(b => !isTest(b));

    const confirmPay = async ({ amount, paidAt, method, txn, file, notes }) => {
        const proof = await api.uploadProof(file, `bonus_${payFor.id}`);
        try {
            await api.markBonusPaid({ bonusId: payFor.id, amount, paidAt, method, txn, proof, notes });
            showToast(`Bonus paid · ${money(amount)} to ${ctx.staffName(payFor.staff_id)}`);
            const id = payFor.id;
            setPayFor(null);
            await ctx.reload();
            setDetailId(id);
        } catch (e) { await api.removeProof(proof); throw e; }
    };

    const doDelete = async () => {
        try { await api.deleteBonus(deleting.id); showToast("Bonus deleted"); setDeleting(null); await ctx.reload(); }
        catch (e) { showToast(e.message, T.danger); }
    };

    const cols = [
        { key: "emp", header: "Employee", sortValue: b => ctx.staffName(b.staff_id).toLowerCase(), exportValue: b => ctx.staffName(b.staff_id),
            render: b => <div><strong style={{ color: T.textStrong, fontWeight: 600 }}>{ctx.staffName(b.staff_id)}</strong>{isTest(b) && <div style={{ marginTop: 4 }}><StatusBadge status="draft" label="Test account" /></div>}</div> },
        { key: "amount", header: "Bonus", align: "right", sortValue: b => Number(b.amount), exportValue: b => b.amount, render: b => <span style={text.money}>{money(b.amount)}</span> },
        { key: "reason", header: "Reason", hideOnMobile: true, exportValue: b => b.reason || "", render: b => b.reason || <span style={text.caption}>—</span> },
        { key: "created", header: "Created", nowrap: true, sortValue: b => b.date || b.created_at || "", exportValue: b => b.date || "", render: b => fmtDay(b.date || b.created_at) },
        { key: "status", header: "Status", exportValue: bonusLabel, render: b => <StatusBadge status={bonusBadge(b)} label={bonusLabel(b)} /> },
        { key: "paid", header: "Paid date", nowrap: true, sortValue: b => b.paid_at || "", exportValue: b => (b.paid_at ? fmtDay(b.paid_at) : ""), render: b => (b.paid_at ? fmtDay(b.paid_at) : <span style={text.caption}>—</span>) },
    ];

    const filters = (
        <>
            <Select value={fStaff} onChange={e => setFStaff(e.target.value)} style={{ width: mobile ? "100%" : 180 }}>
                <option value="all">All employees</option>
                {ctx.staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
            <Select value={fYear} onChange={e => setFYear(e.target.value)} style={{ width: mobile ? "100%" : 120 }}>
                <option value="all">All years</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
        </>
    );

    return (
        <div>
            <PageHeader title="Bonuses" subtitle="Create bonuses, then record each payment when it is released"
                actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }}>+ Add bonus</Button>} />
            <StatGrid>
                <FinanceStatCard label="Pending" value={money(sum(real(pending)))} sub={`${real(pending).length} bonus${real(pending).length === 1 ? "" : "es"}`} tone={T.warning} />
                <FinanceStatCard label="Paid" value={money(sum(real(paid), b => b.paid_amount ?? b.amount))} sub={`${real(paid).length} paid`} tone={T.success} />
                <FinanceStatCard label="This month" value={money(sum(real(thisMonth)))} sub={period(now.getFullYear(), now.getMonth() + 1)} tone={T.primary} />
                <FinanceStatCard label="Employees rewarded" value={new Set(real(all).map(b => b.staff_id)).size} sub="all time · excl. test accounts" />
            </StatGrid>
            <Tabs value={tab} onChange={setTab} items={[["pending", "Pending", pending.length], ["paid", "Paid", paid.length], ["all", "All", all.length]]} />
            <DataTable columns={cols} rows={rows} searchText={b => `${ctx.staffName(b.staff_id)} ${b.reason || ""} ${b.txn_number || ""}`}
                searchPlaceholder="Search employee or reason…" filters={filters} defaultSort={{ key: "created", dir: "desc" }}
                onRowClick={b => setDetailId(b.id)}
                toolbarRight={<ExportMenu filename={`bonuses_${tab}`} title="Bonuses" columns={cols} rows={rows} />}
                rowActions={b => isPaid(b) ? <>
                    <Button size="sm" variant="secondary" onClick={() => setDetailId(b.id)}>Details</Button>
                    <ActionMenu items={[{ label: "Download slip", onClick: () => slip(ctx, b) }, b.payment_proof && { label: "View proof", onClick: () => openProof(b.payment_proof) }]} />
                </> : <>
                    <Button size="sm" variant="success" onClick={() => setPayFor(b)}>Mark as paid</Button>
                    <ActionMenu items={[
                        { label: "Edit", onClick: () => { setEditing(b); setFormOpen(true); } },
                        { label: "Download slip", onClick: () => slip(ctx, b) },
                        { label: "Delete", danger: true, onClick: () => setDeleting(b) },
                    ]} />
                </>}
                mobileCard={b => (
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: T.textStrong }}>{ctx.staffName(b.staff_id)}</div>
                            <div style={{ ...text.caption, overflow: "hidden", textOverflow: "ellipsis" }}>{b.reason || "Bonus"} · {fmtDay(b.date || b.created_at)}</div>
                            <div style={{ marginTop: 6 }}><StatusBadge status={bonusBadge(b)} label={isPaid(b) && b.paid_at ? `${bonusLabel(b)} · ${fmtDay(b.paid_at)}` : bonusLabel(b)} /></div>
                        </div>
                        <div style={{ ...text.money, fontSize: 16, whiteSpace: "nowrap" }}>{money(b.amount)}</div>
                    </div>
                )}
                empty={tab === "pending" ? "No pending bonuses." : "No bonuses yet."} />

            <BonusFormModal open={formOpen} ctx={ctx} editing={editing} onClose={() => { setFormOpen(false); setEditing(null); }} />
            <PaymentModal open={!!payFor} title="Mark bonus as paid" subtitle={payFor ? `${ctx.staffName(payFor.staff_id)} · ${payFor.reason || "Bonus"}` : ""}
                methods={METHODS} defaultAmount={payFor?.amount} amountHint={payFor ? `Bonus amount: ${money(payFor.amount)}` : undefined}
                summary={payFor && <div style={{ ...surface.inset, padding: 14 }}><DetailList items={[["Bonus", <strong>{money(payFor.amount)}</strong>], ["Bonus date", fmtDay(payFor.date)]]} />
                    <div style={{ ...text.caption, marginTop: 8 }}>After confirming, the payment is finalized and can no longer be edited or deleted.</div></div>}
                onConfirm={confirmPay} onClose={() => setPayFor(null)} />
            <BonusDrawer ctx={ctx} bonus={detail} onClose={() => setDetailId(null)} />
            <Modal open={!!deleting} title="Delete bonus?" onClose={() => setDeleting(null)} width={420}
                footer={<><Button variant="secondary" onClick={() => setDeleting(null)}>Cancel</Button><Button variant="danger" onClick={doDelete}>Delete</Button></>}>
                {deleting && <div style={{ fontSize: 14 }}>{money(deleting.amount)} for {ctx.staffName(deleting.staff_id)}{deleting.reason ? ` (${deleting.reason})` : ""} will be removed. Only unpaid bonuses can be deleted.</div>}
            </Modal>
        </div>
    );
}

// ─── Staff: My Bonus ─────────────────────────────────────────────────────────
export function MyBonusPage({ ctx }) {
    const [tab, setTab] = useState("pending");
    const [detailId, setDetailId] = useState(null);
    const mine = ctx.bonuses.filter(b => b.staff_id === ctx.user.id);
    const pending = mine.filter(b => !isPaid(b));
    const paid = mine.filter(isPaid);
    const sum = (list, f = (b) => b.amount) => list.reduce((a, b) => a + Number(f(b) || 0), 0);
    const detail = detailId ? mine.find(b => b.id === detailId) : null;
    const cols = [
        { key: "amount", header: "Bonus", align: "right", sortValue: b => Number(b.amount), render: b => <span style={text.money}>{money(b.amount)}</span> },
        { key: "reason", header: "Reason", render: b => b.reason || "—" },
        { key: "created", header: "Date", nowrap: true, sortValue: b => b.date || "", render: b => fmtDay(b.date || b.created_at) },
        { key: "status", header: "Status", render: b => <StatusBadge status={bonusBadge(b)} label={bonusLabel(b)} /> },
        { key: "paid", header: "Paid", render: b => (b.paid_at ? <div>{fmtDay(b.paid_at)}<div style={text.caption}>{methodLabel(b.payment_method)}</div></div> : "—") },
    ];
    return (
        <div>
            <PageHeader title="My bonus" subtitle="Your bonus payments from Foxbeep" />
            <StatGrid>
                <FinanceStatCard label="Total" value={money(sum(mine))} tone={T.primary} />
                <FinanceStatCard label="Pending" value={money(sum(pending))} sub={`${pending.length} pending`} tone={T.warning} />
                <FinanceStatCard label="Paid" value={money(sum(paid, b => b.paid_amount ?? b.amount))} sub={`${paid.length} paid`} tone={T.success} />
            </StatGrid>
            <Tabs value={tab} onChange={setTab} items={[["pending", "Pending", pending.length], ["paid", "Paid", paid.length]]} />
            <DataTable columns={cols} rows={tab === "pending" ? pending : paid} defaultSort={{ key: "created", dir: "desc" }}
                onRowClick={b => setDetailId(b.id)}
                rowActions={b => <>
                    <Button size="sm" variant="secondary" onClick={() => slip(ctx, b)}>Slip</Button>
                    {b.payment_proof && <ActionMenu items={[{ label: "View payment proof", onClick: () => openProof(b.payment_proof) }]} />}
                </>}
                empty={tab === "pending" ? "No pending bonuses." : "No paid bonuses yet."} />
            <BonusDrawer ctx={ctx} bonus={detail} onClose={() => setDetailId(null)} />
        </div>
    );
}
